import { Injectable } from "@angular/core";
import { JwtPayloadService } from "./jwt-payload.service";

/**
 * Serviço centralizado de permissões baseado em JWT.
 *
 * Decodifica o token JWT armazenado em sessionStorage e expõe métodos
 * para verificar as permissões do usuário logado. As permissões são
 * organizadas por segmento (vendas, relatorios, gestao, etc.) e incluídas
 * no payload do JWT pelo backend durante o login, dentro do objeto `roles`.
 *
 * @example
 * // Verificar permissão com dot notation
 * if (this.permissionService.hasPermission('vendas.cheque')) { ... }
 *
 * // Obter segmento inteiro
 * const vendas = this.permissionService.getSegment('vendas');
 *
 * // Obter todas as permissões
 * const perms = this.permissionService.getPermissions();
 */
@Injectable({
  providedIn: "root",
})
export class PermissionService {
  constructor(private jwtPayload: JwtPayloadService) {}

  /**
   * Verifica se o usuário possui uma determinada permissão usando dot notation.
   * Navega o objeto aninhado de permissões separando por '.' (ex: 'vendas.pecas').
   *
   * @param permission - Caminho da permissão em dot notation (ex: 'vendas.cheque', 'gestao.acesso')
   * @returns `true` se a permissão estiver ativa (valor truthy), `false` caso contrário
   */
  hasPermission(permission: string): boolean {
    const roles = this.getRolesFromToken();
    const parts = permission.split('.');
    let current: any = roles;
    for (const part of parts) {
      if (!current || typeof current !== 'object') return false;
      current = current[part];
    }
    return !!current;
  }

  /**
   * Verifica se o usuário possui pelo menos uma das permissões informadas.
   *
   * @param permissions - Lista de caminhos em dot notation
   * @returns `true` se ao menos uma permissão estiver ativa
   */
  hasAnyPermission(permissions: string[]): boolean {
    return permissions.some(p => this.hasPermission(p));
  }

  /**
   * Retorna o objeto de um segmento específico de permissões.
   *
   * @param segment - Nome do segmento (ex: 'vendas', 'gestao', 'relatorios')
   * @returns Objeto com as permissões do segmento ou objeto vazio
   */
  getSegment(segment: string): any {
    const roles = this.getRolesFromToken();
    return roles[segment] || {};
  }

  /**
   * Retorna o objeto completo de permissões aninhado extraído do JWT.
   *
   * @returns Objeto aninhado com todas as permissões do usuário
   */
  getPermissions(): any {
    return this.getRolesFromToken();
  }

  /**
   * Verifica se o usuário logado pode lançar peças v2 do dia anterior para a loteria informada.
   * @param loteriaId - ID da loteria
   * @returns true se lancamentos.lancamento_anterior[loteriaId] estiver ativo
   */
  hasLancamentoAnteriorLoteria(loteriaId: number | string): boolean {
    const map = this.getSegment('lancamentos').lancamento_anterior;
    if (!map || typeof map !== 'object') {
      return false;
    }
    const key = String(loteriaId);
    return !!(map[key] || map[loteriaId as keyof typeof map]);
  }

  /**
   * Normaliza a estrutura de permissões, garantindo compatibilidade
   * com JWTs gerados antes das reestruturações.
   * - Migra chaves legadas de vendas para o segmento lancamentos
   * - Expande cambio.acesso em permissões individuais quando necessário
   *
   * @param perms - Objeto de permissões extraído do JWT
   * @returns Permissões normalizadas
   */
  private normalizePermissions(perms: any): any {
    if (!perms || typeof perms !== 'object') return perms;

    // Normalizar vendas → lancamentos
    if (!perms.lancamentos && perms.vendas) {
      const vendas = perms.vendas;
      perms.lancamentos = {
        header: vendas.header || false,
        pecas: !!vendas.pecas_lanc,
        premios: !!vendas.premios_lanc,
        descarga: !!vendas.descarga_lanc,
        mapa: !!vendas.mapa,
        resultados: !!vendas.resultados,
      };

      delete vendas.pecas_lanc;
      delete vendas.premios_lanc;
      delete vendas.descarga_lanc;
      delete vendas.mapa;
      delete vendas.resultados;
    }

    // Normalizar cambio: expandir acesso em permissões individuais
    if (perms.cambio && perms.cambio.vendedores === undefined) {
      const acesso = !!perms.cambio.acesso;
      perms.cambio.vendedores = acesso;
      perms.cambio.ferias = acesso;
      perms.cambio.premios = acesso;
      perms.cambio.deu = acesso;
      perms.cambio.envios = acesso;
      perms.cambio.extrato = acesso;
      perms.cambio.rela_ferias = acesso;
    }

    // Normalizar descarga: expandir acesso em permissões individuais
    if (perms.descarga && perms.descarga.clientes === undefined) {
      const dAccesso = !!perms.descarga.acesso;
      perms.descarga.clientes = dAccesso;
      perms.descarga.envio = dAccesso;
      perms.descarga.premios = dAccesso;
      perms.descarga.deu = dAccesso;
      perms.descarga.coringa = dAccesso;
      perms.descarga.relatorio = dAccesso;
      perms.descarga.finalizar_desc = dAccesso;
      perms.descarga.destino = dAccesso;
      perms.descarga.origem = dAccesso;
    }

    if (!perms.lancamentos) {
      perms.lancamentos = { header: false };
    }
    if (!perms.lancamentos.lancamento_anterior || typeof perms.lancamentos.lancamento_anterior !== 'object') {
      perms.lancamentos.lancamento_anterior = {};
    }

    return perms;
  }

  /**
   * Extrai o objeto `roles` do payload do JWT armazenado em sessionStorage.
   * Aplica normalização para garantir compatibilidade com estruturas antigas.
   *
   * @returns Objeto de permissões aninhado ou objeto vazio se o token não existir
   */
  private getRolesFromToken(): any {
    try {
      const token = sessionStorage.getItem("x");
      if (!token) return {};
      const payload = this.jwtPayload.getMergedPayload(token) as { roles?: unknown };
      return this.normalizePermissions(payload.roles || {});
    } catch (e) {
      return {};
    }
  }
}
